import React from 'react';
import { Phone, User, Clock, Users } from 'lucide-react';
import type { ActiveCallMonitoring, SupervisionType } from '../../lib/types/supervisor-monitoring';
import { useSupervisionTypeDescription } from '../../lib/hooks/useSupervisorMonitoring';

interface ActiveCallCardProps {
  call: ActiveCallMonitoring;
  onMonitor: (callId: string, conferenceName: string) => void;
  isMonitoring: boolean;
  currentSupervisionType?: SupervisionType;
}

export function ActiveCallCard({
  call,
  onMonitor,
  isMonitoring,
  currentSupervisionType,
}: ActiveCallCardProps) {
  const monitoringDescription = currentSupervisionType
    ? useSupervisionTypeDescription(currentSupervisionType)
    : '';

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
      case 'in-progress':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'ringing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSupervisionBadge = (): JSX.Element | null => {
    if (call.current_supervision_type === 'none') return null;

    const colors: Record<SupervisionType, string> = {
      monitor: 'bg-blue-100 text-blue-800 border-blue-200',
      barge: 'bg-purple-100 text-purple-800 border-purple-200',
      whisper: 'bg-orange-100 text-orange-800 border-orange-200',
      takeover: 'bg-red-100 text-red-800 border-red-200',
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
          colors[call.current_supervision_type as SupervisionType]
        }`}
      >
        {call.current_supervision_type.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 hover:shadow-md transition-shadow">
      {/* Header with Status and Badges */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(
              call.call_status
            )}`}
          >
            {call.call_status}
          </span>
          {getSupervisionBadge()}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <Clock className="w-4 h-4 mr-1" />
          {formatDuration(call.call_duration_seconds)}
        </div>
      </div>

      {/* Call Information */}
      <div className="space-y-2 mb-4">
        {/* Agent Info */}
        <div className="flex items-center text-sm">
          <User className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
          <div className="flex-1">
            <span className="font-medium text-gray-900">
              {call.agent_name || 'Unknown Agent'}
            </span>
            {call.agent_email && (
              <span className="text-gray-500 ml-2 text-xs">
                {call.agent_email}
              </span>
            )}
          </div>
        </div>

        {/* Customer Info */}
        <div className="flex items-center text-sm">
          <Phone className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
          <div className="flex-1">
            {call.customer_name && (
              <span className="font-medium text-gray-900 mr-2">
                {call.customer_name}
              </span>
            )}
            <span className="text-gray-600">
              {call.customer_phone || 'Unknown Number'}
            </span>
          </div>
        </div>

        {/* Queue Info */}
        {call.queue_name && (
          <div className="flex items-center text-sm">
            <Users className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
            <span className="text-gray-600">{call.queue_name}</span>
          </div>
        )}
      </div>

      {/* Action Button */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
        {isMonitoring && currentSupervisionType ? (
          <div className="flex-1">
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">Monitoring: </span>
              {monitoringDescription}
            </p>
          </div>
        ) : (
          <button
            onClick={() => onMonitor(call.call_id, call.conference_name || '')}
            disabled={!call.conference_name || call.is_being_monitored}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            {call.is_being_monitored ? 'Being Monitored' : 'Start Monitoring'}
          </button>
        )}
      </div>

      {/* Call Details Footer */}
      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
        <span>Call ID: {call.call_sid.substring(0, 12)}...</span>
        <span className="capitalize">{call.direction}</span>
      </div>
    </div>
  );
}
